import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { blockMenuLabel } from 'src/helpers/pageBlockMenuLabels';
import { ALL_PAGE_BLOCK_TYPES, NESTED_BLOCK_TYPES } from 'src/helpers/pageBlockFactory';
import { PageBlockType } from 'src/interfaces/PageBlock';

const ROOT_ADD_TYPES = ALL_PAGE_BLOCK_TYPES.filter(type => type !== 'columns');

interface AddBlockMenuProps {
  canAddColumns?: boolean;
  disabledTypes?: Partial<Record<PageBlockType, boolean>>;
  disabledTitles?: Partial<Record<PageBlockType, string>>;
  onAdd: (type: PageBlockType, columnCount?: 2 | 3 | 4) => void;
  buttonLabel?: string;
  buttonSize?: 'small' | 'medium';
  buttonVariant?: 'text' | 'outlined';
  showButtonIcon?: boolean;
}

export default function AddBlockMenu({
  canAddColumns = false,
  disabledTypes = {},
  disabledTitles = {},
  onAdd,
  buttonLabel,
  buttonSize = 'medium',
  buttonVariant = 'outlined',
  showButtonIcon = true,
}: AddBlockMenuProps) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);
  const types = canAddColumns ? ROOT_ADD_TYPES : NESTED_BLOCK_TYPES;

  return (
    <>
      <Button
        color="primary"
        variant={buttonVariant}
        size={buttonSize}
        sx={buttonVariant === 'text' ? { minWidth: 0, whiteSpace: 'nowrap' } : undefined}
        startIcon={showButtonIcon ? <AddIcon /> : undefined}
        aria-label={buttonLabel ?? t('system.pages.addBlock')}
        aria-controls={open ? 'add-block-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={e => setAnchor(e.currentTarget)}
      >
        {buttonLabel ?? t('system.pages.addBlock')}
      </Button>
      <Menu
        id="add-block-menu"
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {types.map(type => {
          const disabled = disabledTypes[type];
          return (
            <MenuItem
              key={type}
              disabled={disabled}
              onClick={() => {
                onAdd(type);
                setAnchor(null);
              }}
              title={disabled ? disabledTitles[type] : undefined}
            >
              {blockMenuLabel(type, t)}
            </MenuItem>
          );
        })}
        {canAddColumns ? (
          <>
            <MenuItem disabled sx={{ opacity: 1, fontWeight: 600 }}>
              {t('system.pages.addColumnsBlock')}
            </MenuItem>
            {([2, 3, 4] as const).map(count => (
              <MenuItem
                key={`columns-${count}`}
                sx={{ pl: 3 }}
                onClick={() => {
                  onAdd('columns', count);
                  setAnchor(null);
                }}
              >
                {t(`system.pages.columnsCount${count}`)}
              </MenuItem>
            ))}
          </>
        ) : null}
      </Menu>
    </>
  );
}
